import {
  Component, Input, ElementRef, ViewChild, OnChanges, OnDestroy, AfterViewInit,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Chart } from 'chart.js';

import { chartPalette, timeAxisTick, dragZoom, lineLegendLabels } from './chart.utils';

/** Per-metric presentation, mirroring the backend ChartSvg so charts look consistent. */
interface MetricMeta { label: string; labelEn: string; unit: string; color: string; icon: string; }

const META: { [k: string]: MetricMeta } = {
  pvYieldW:          { label: 'PV-Ertrag',         labelEn: 'PV yield',          unit: 'W',  color: '#f5a623', icon: '☀️' },
  batteryPercent:    { label: 'Batterie',          labelEn: 'Battery',           unit: '%',  color: '#43d977', icon: '🔋' },
  batteryDischargeW: { label: 'Batterie-Leistung', labelEn: 'Battery power',      unit: 'W',  color: '#43d977', icon: '🔋' },
  gridConsumptionW:  { label: 'Netzbezug',         labelEn: 'Grid draw',         unit: 'W',  color: '#ff5c5c', icon: '🔌' },
  totalConsumptionW: { label: 'Hausverbrauch',     labelEn: 'House consumption',  unit: 'W',  color: '#6c63ff', icon: '🏠' },
  consumptionL1W:    { label: 'Verbrauch L1',      labelEn: 'Consumption L1',    unit: 'W',  color: '#6c63ff', icon: '🏠' },
  consumptionL2W:    { label: 'Verbrauch L2',      labelEn: 'Consumption L2',    unit: 'W',  color: '#8a7dff', icon: '🏠' },
  consumptionL3W:    { label: 'Verbrauch L3',      labelEn: 'Consumption L3',    unit: 'W',  color: '#a99dff', icon: '🏠' },
  boilerTempC:       { label: 'Kesseltemperatur',  labelEn: 'Boiler temp',       unit: '°C', color: '#ff7043', icon: '🔥' },
  exhaustTempC:      { label: 'Abgastemperatur',   labelEn: 'Exhaust temp',      unit: '°C', color: '#ff8a65', icon: '💨' },
  bufferChargePct:   { label: 'Pufferspeicher',    labelEn: 'Buffer charge',     unit: '%',  color: '#ffb300', icon: '🌡️' },
  bufferTopTempC:    { label: 'Puffer oben',       labelEn: 'Buffer top',        unit: '°C', color: '#ff7043', icon: '🌡️' },
  bufferBottomTempC: { label: 'Puffer unten',      labelEn: 'Buffer bottom',     unit: '°C', color: '#42a5f5', icon: '🌡️' },
  hotWaterTempC:     { label: 'Warmwasser',        labelEn: 'Hot water',         unit: '°C', color: '#29b6f6', icon: '🚿' },
  outdoorTempC:      { label: 'Außentemperatur',   labelEn: 'Outdoor temp',      unit: '°C', color: '#26c6da', icon: '🌳' },
  // the first heating circuit's flow (Heizkreis 01) — not a boiler / system flow
  flowTempActualC:   { label: 'Vorlauf Heizkreis 01', labelEn: 'Flow heating circuit 01', unit: '°C', color: '#ef5350', icon: '♨️' },
  solarThermalTempC: { label: 'Solarthermie',      labelEn: 'Solar thermal',     unit: '°C', color: '#ffa726', icon: '☀️' },
  circulationPumpPct: { label: 'Zirkulationspumpe', labelEn: 'Circulation pump', unit: '%', color: '#0d6efd', icon: '🔄' },
  bufferPumpPct:     { label: 'Pufferladepumpe',   labelEn: 'Buffer loading pump', unit: '%', color: '#6f42c1', icon: '🛢️' },
  dhwPumpPct:        { label: 'Boilerladepumpe (Warmwasser)', labelEn: 'Hot-water tank pump', unit: '%', color: '#12b886', icon: '🚿' },
  dhwSolarRefTempC:  { label: 'Warmwasser unten',  labelEn: 'Hot water bottom',  unit: '°C', color: '#66d9e8', icon: '🚿' },
  oxygenPct:         { label: 'Restsauerstoff',    labelEn: 'Residual oxygen',   unit: '%',  color: '#0ca678', icon: '💨' },
  draftFanPct:       { label: 'Saugzug',           labelEn: 'Draft fan',         unit: '%',  color: '#7048e8', icon: '💨' },
  solarThermalPumpPct: { label: 'Kollektorpumpe',  labelEn: 'Collector pump',    unit: '%',  color: '#f59f00', icon: '☀️' },
  collectorFlowTempC:  { label: 'Kollektor Vorlauf', labelEn: 'Collector flow',  unit: '°C', color: '#c2255c', icon: '☀️' },
  collectorReturnTempC: { label: 'Kollektor Rücklauf', labelEn: 'Collector return', unit: '°C', color: '#5c7cfa', icon: '☀️' },
  circulationReturnTempC: { label: 'Zirkulation Rücklauf', labelEn: 'Circulation return', unit: '°C', color: '#339af0', icon: '🔄' },
  storeRemainingT:   { label: 'Pelletlager',       labelEn: 'Pellet store',      unit: 't',  color: '#795548', icon: '🪵' },
  // the boiler's own ~44 kg hopper (refilled from the store by suction) — not the store / silo
  pelletLevelPct:    { label: 'Pelletbehälter (Kessel)', labelEn: 'Boiler pellet hopper', unit: '%', color: '#8d6e63', icon: '🪵' },
  pelletConsumptionKg: { label: 'Pelletzähler (gesamt)', labelEn: 'Pellet counter (total)', unit: 'kg', color: '#795548', icon: '🪵' },
  levelPct:          { label: 'Zisterne',          labelEn: 'Cistern',           unit: '%',  color: '#2196f3', icon: '💧' },
  depthCm:           { label: 'Wasserstand',       labelEn: 'Water level',       unit: 'cm', color: '#2196f3', icon: '💧' },
  tempC:             { label: 'Temperatur',        labelEn: 'Temperature',       unit: '°C', color: '#ff7043', icon: '🌡️' },
  humidity:          { label: 'Luftfeuchtigkeit',  labelEn: 'Humidity',          unit: '%',  color: '#29b6f6', icon: '💧' },
};

const RANGE_MS: { [k: string]: number } = {
  day:   24 * 3600 * 1000,
  week:  7  * 24 * 3600 * 1000,
  month: 30 * 24 * 3600 * 1000,
  year:  365 * 24 * 3600 * 1000,
};
const RANGE_LABEL: { [k: string]: { de: string; en: string } } = {
  day:   { de: 'Letzte 24 Stunden', en: 'Last 24 hours' },
  week:  { de: 'Letzte 7 Tage',     en: 'Last 7 days' },
  month: { de: 'Letzter Monat',     en: 'Last month' },
  year:  { de: 'Letztes Jahr',      en: 'Last year' },
  custom:{ de: 'Zeitraum',          en: 'Time range' },
};

/** Display precision per unit (the tooltip shows one decimal at most): the y axis' `resolution`, from which
 *  the shared yMinSpan plugin derives the axis' minimum span, so quantisation noise stays flat. */
const UNIT_RESOLUTION: { [unit: string]: number } = { '°C': 0.1, '%': 1, 'W': 1, 'kg': 0.1, 'cm': 1, 't': 0.1 };

/** y-axis `resolution` of a unit (the option is read by the yMinSpan plugin; Chart.js' types lack it). */
function axisResolution(unit: string): {} {
  const resolution = UNIT_RESOLUTION[unit];
  return resolution ? { resolution } : {};
}

/** Distinct line colours used when several co-plotted metrics would otherwise share a hue. */
const SERIES_COLORS = ['#6c63ff', '#43d977', '#ff5c5c', '#29b6f6', '#ffb300', '#ab47bc', '#26a69a', '#ec407a'];

/**
 * Reusable data chart rendered client-side with Chart.js (the same library as the
 * dashboard, via the shared chart.utils palette/zoom). Fetches a series from
 * /api/history and draws one or more metrics with hover tooltips and drag/pinch zoom.
 * Multiple metrics of the same series (e.g. buffer top + bottom) are drawn as separate
 * lines, with a second y-axis when their units differ. The window is either a relative
 * `range` keyword or an absolute `from`/`to` (epoch ms). Used by Claudia to post charts
 * into the chat and reusable anywhere in the app.
 */
@Component({
  selector: 'app-data-chart',
  template: `
    <div class="data-chart" [style.height.px]="height">
      <canvas #cv></canvas>
    </div>
  `,
  styles: [`
    .data-chart { position: relative; width: 100%; background: rgba(0,0,0,0.12); border-radius: 12px; padding: 6px; }
  `],
})
export class DataChartComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() series = '';
  @Input() metric = '';                 // single metric (legacy / primary)
  @Input() metrics: string[] = [];      // multiple metrics drawn as separate lines (overrides metric)
  @Input() range: 'day' | 'week' | 'month' | 'year' | 'custom' = 'day';
  @Input() from?: number;               // absolute window start (epoch ms); overrides range
  @Input() to?: number;                 // absolute window end (epoch ms)
  @Input() height = 240;
  @Input() darkMode = true;
  @Input() label?: string;   // optional title override (e.g. the room name for a single-metric thermometer)
  @Input() lang: 'de' | 'en' = 'de';

  @ViewChild('cv', { static: true }) canvas!: ElementRef<HTMLCanvasElement>;

  private chart: Chart | null = null;
  private viewReady = false;

  constructor(private http: HttpClient) {}

  ngAfterViewInit(): void { this.viewReady = true; this.reload(); }
  ngOnChanges(): void { if (this.viewReady) this.reload(); }
  ngOnDestroy(): void { this.chart?.destroy(); }

  /** The list of metrics to plot (the `metrics` array wins; otherwise the single `metric`). */
  private metricList(): string[] {
    const list = this.metrics?.length ? this.metrics : (this.metric ? [this.metric] : []);
    return list.map(s => s.trim()).filter(Boolean);
  }

  private meta(metric: string): MetricMeta {
    return META[metric] ?? { label: metric, labelEn: metric, unit: '', color: '#6c63ff', icon: '📈' };
  }

  /** Dataset label in the active language; the room-name override applies only to a lone metric. */
  private datasetLabel(m: MetricMeta, count: number): string {
    if (count === 1 && this.label) return this.label;
    return this.lang === 'en' ? m.labelEn : m.label;
  }

  /** The time window: an explicit absolute from/to if given, else the relative range ending now. */
  private window(): { from: number; to: number } {
    if (this.from && this.to && this.to > this.from) return { from: this.from, to: this.to };
    const span = RANGE_MS[this.range] ?? RANGE_MS['day'];
    const to = Date.now();
    return { from: to - span, to };
  }

  /** Down-sample bucket count scaled to the window (≈ one point per 5 min, clamped 60–400). */
  private buckets(span: number): number {
    return Math.min(400, Math.max(60, Math.round(span / (5 * 60 * 1000))));
  }

  private reload(): void {
    const metricList = this.metricList();
    if (!this.series || !metricList.length) return;
    const win = this.window();
    const url = `/api/history?series=${encodeURIComponent(this.series)}`
      + `&from=${win.from}&to=${win.to}&buckets=${this.buckets(win.to - win.from)}`;

    this.http.get<any[]>(url).subscribe({
      next: (points) => this.draw(points || []),
      error: () => this.draw([]),
    });
  }

  private draw(points: any[]): void {
    const metricList = this.metricList();
    const metas = metricList.map(mk => this.meta(mk));
    const primaryUnit = metas[0]?.unit ?? '';
    const single = metricList.length === 1;

    // Keep co-plotted lines visually distinct: if a metric's preferred colour is already taken,
    // fall back to the next free colour from the palette.
    const used = new Set<string>();
    let fallbackIdx = 0;
    const pickColor = (preferred: string): string => {
      let c = preferred;
      if (used.has(c.toLowerCase())) {
        while (fallbackIdx < SERIES_COLORS.length && used.has(SERIES_COLORS[fallbackIdx].toLowerCase())) fallbackIdx++;
        c = SERIES_COLORS[fallbackIdx] ?? preferred;
      }
      used.add(c.toLowerCase());
      return c;
    };

    const datasets = metricList.map((mk, i) => {
      const m = metas[i];
      const color = pickColor(m.color);
      const data = points
        .filter(p => typeof p?.ts === 'number' && typeof p?.[mk] === 'number')
        .map(p => ({ x: p.ts as number, y: p[mk] as number }));
      const secondary = m.unit !== primaryUnit;   // a differing-unit line gets its own right axis
      return {
        label: `${m.icon} ${this.datasetLabel(m, metricList.length)}${m.unit ? ' (' + m.unit + ')' : ''}`,
        linkKey: mk,
        data,
        borderColor: color,
        backgroundColor: single ? this.gradient(color) : 'transparent',
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 4,
        tension: 0.25,
        fill: single,
        yAxisID: secondary ? 'y1' : 'y',
      };
    });
    const hasSecondary = datasets.some(d => d.yAxisID === 'y1');
    const secondaryUnit = metas.find(m => m.unit !== primaryUnit)?.unit ?? '';

    const pal = chartPalette(this.darkMode);
    const win = this.window();
    const tickFmt = timeAxisTick(win.from, win.to);

    this.chart?.destroy();
    this.chart = new Chart(this.canvas.nativeElement, {
      type: 'line',
      data: { datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { display: true, labels: lineLegendLabels(pal.textColor) },
          tooltip: {
            callbacks: {
              title: (items) => {
                const ts = items[0]?.parsed?.x;
                return ts != null ? new Date(ts).toLocaleString(this.lang === 'en' ? 'en-GB' : 'de-DE', {
                  day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
                }) : '';
              },
              label: (item) => {
                const m = metas[item.datasetIndex];
                const name = single ? '' : (this.lang === 'en' ? m.labelEn : m.label) + ': ';
                return ` ${name}${this.fmt(item.parsed.y ?? 0)} ${m?.unit ?? ''}`.trimEnd();
              },
            },
          },
          zoom: dragZoom(),
        },
        scales: {
          x: {
            type: 'linear',
            min: win.from, max: win.to,
            ticks: { color: pal.textColor, maxRotation: 0, autoSkip: true,
                     callback: (val) => tickFmt(val as number) },
            grid: { color: pal.gridColor },
          },
          y: {
            position: 'left',
            ...axisResolution(primaryUnit),
            title: { display: hasSecondary && !!primaryUnit, text: primaryUnit, color: pal.textColor },
            ticks: { color: pal.textColor, callback: (val) => this.fmt(val as number) },
            grid: { color: pal.gridColor },
          },
          ...(hasSecondary ? {
            y1: {
              position: 'right' as const,
              ...axisResolution(secondaryUnit),
              title: { display: !!secondaryUnit, text: secondaryUnit, color: pal.textColor },
              ticks: { color: pal.textColor, callback: (val: number | string) => this.fmt(val as number) },
              grid: { drawOnChartArea: false },
            },
          } : {}),
        },
      },
    });
  }

  private gradient(color: string): any {
    const ctx = this.canvas.nativeElement.getContext('2d');
    if (!ctx) return color;
    const g = ctx.createLinearGradient(0, 0, 0, this.height);
    g.addColorStop(0, this.hexA(color, 0.35));
    g.addColorStop(1, this.hexA(color, 0));
    return g;
  }

  /** Hex (#rrggbb) → rgba string with the given alpha. */
  private hexA(hex: string, a: number): string {
    const h = hex.replace('#', '');
    const r = parseInt(h.substring(0, 2), 16);
    const g = parseInt(h.substring(2, 4), 16);
    const b = parseInt(h.substring(4, 6), 16);
    return `rgba(${r},${g},${b},${a})`;
  }

  private fmt(v: number): string {
    return Math.abs(v - Math.round(v)) < 1e-6 ? String(Math.round(v)) : v.toFixed(1);
  }

  get periodLabel(): string {
    const r = RANGE_LABEL[this.range] ?? RANGE_LABEL['day'];
    return this.lang === 'en' ? r.en : r.de;
  }
}
